import { Text, Stack } from '@mantine/core';
import ReactMarkdown from 'react-markdown';
import { useMantineColorScheme } from '@mantine/core';
import { Message } from '../useScoutChat';

interface ScoutChatMessageProps {
  message: Message;
  isThinking?: boolean;
  isToolCall?: boolean;
  shouldShimmer?: boolean;
  className?: string;
  style?: React.CSSProperties;
  messageStyle?: React.CSSProperties;
}

export const ScoutChatMessage = ({
  message,
  isThinking = false,
  isToolCall = false,
  shouldShimmer = false,
  className,
  style,
  messageStyle,
}: ScoutChatMessageProps) => {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  return (
    <div
      data-message-id={message.id}
      style={{
        display: 'flex',
        justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
        marginBottom: 'var(--mantine-spacing-sm)',
        ...style,
      }}
      className={className}
    >
      <div
        style={{
          padding: 'var(--mantine-spacing-sm)',
          borderRadius:
            message.role === 'user'
              ? '1.5rem 1.5rem 0 1.5rem' // no radius on bottom-right for user
              : '1.5rem 1.5rem 1.5rem 0', // no radius on bottom-left for AI
          background: isDark ? 'var(--mantine-color-dark-7)' : 'white',
          maxWidth: '85%',
          boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
          ...messageStyle,
        }}
      >
        <Text fw={600} size='sm' c='dimmed' mb={4}>
          {message.role === 'user' ? 'You' : 'Scout'}
        </Text>
        {shouldShimmer ? (
          <Text
            size='sm'
            style={{
              whiteSpace: 'pre-wrap',
              color: 'transparent',
              background: isDark
                ? 'linear-gradient(135deg, var(--mantine-color-dark-2), var(--mantine-color-dark-5), var(--mantine-color-dark-2))'
                : 'linear-gradient(135deg, var(--mantine-color-gray-9), var(--mantine-color-gray-6), var(--mantine-color-gray-9))',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundSize: '200% 100%',
              animation: 'shimmer 3s linear infinite',
            }}
          >
            {message.content}
          </Text>
        ) : message.role === 'assistant' ? (
          <div
            style={{
              fontSize: 'var(--mantine-font-size-sm)',
              lineHeight: 1.55,
            }}
          >
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <Text
                    size='sm'
                    style={{ marginBottom: '0.5em', marginTop: 0 }}
                  >
                    {children}
                  </Text>
                ),
                strong: ({ children }) => (
                  <Text component='span' fw={600}>
                    {children}
                  </Text>
                ),
                em: ({ children }) => (
                  <Text component='span' fs='italic'>
                    {children}
                  </Text>
                ),
                code: ({ children }) => (
                  <Text
                    component='code'
                    size='sm'
                    style={{
                      backgroundColor: isDark
                        ? 'var(--mantine-color-dark-6)'
                        : 'var(--mantine-color-gray-1)',
                      padding: '0.125em 0.25em',
                      borderRadius: '0.25rem',
                      fontFamily: 'var(--mantine-font-family-monospace)',
                    }}
                  >
                    {children}
                  </Text>
                ),
                ul: ({ children }) => (
                  <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li style={{ marginBottom: '0.25em' }}>{children}</li>
                ),
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        ) : (
          <Text size='sm' style={{ whiteSpace: 'pre-wrap' }}>
            {message.content}
          </Text>
        )}

        {/* Time Entry Preview Confirmation UI */}
        {message.timeEntryPreview && message.timeEntryPreview.timeEntries && (
          <Stack
            gap='xs'
            mt='md'
            p='sm'
            style={{
              backgroundColor: isDark
                ? 'var(--mantine-color-dark-6)'
                : 'var(--mantine-color-gray-1)',
              borderRadius: '0.5rem',
              border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
            }}
          >
            <Text size='sm' fw={600} mb='xs'>
              Time Entries to Create:
            </Text>
            {message.timeEntryPreview.timeEntries.map((entry, idx) => (
              <div
                key={idx}
                style={{
                  padding: '0.5rem',
                  backgroundColor: isDark
                    ? 'var(--mantine-color-dark-7)'
                    : 'white',
                  borderRadius: '0.25rem',
                  marginBottom: '0.5rem',
                }}
              >
                <Text size='xs' c='dimmed'>
                  Description:
                </Text>
                <Text size='sm' mb='xs'>
                  {entry.description}
                </Text>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div>
                    <Text size='xs' c='dimmed'>
                      Hours:
                    </Text>
                    <Text size='sm'>{entry.hours}</Text>
                  </div>
                  <div>
                    <Text size='xs' c='dimmed'>
                      Rate:
                    </Text>
                    <Text size='sm'>${entry.rate}</Text>
                  </div>
                  <div>
                    <Text size='xs' c='dimmed'>
                      Amount:
                    </Text>
                    <Text size='sm'>${entry.amount}</Text>
                  </div>
                  <div>
                    <Text size='xs' c='dimmed'>
                      Date:
                    </Text>
                    <Text size='sm'>
                      {entry.earnedYmd.year}-
                      {String(entry.earnedYmd.month).padStart(2, '0')}-
                      {String(entry.earnedYmd.day).padStart(2, '0')}
                    </Text>
                  </div>
                </div>
              </div>
            ))}
            <Stack gap='xs' mt='sm'>
              <Text size='xs' c='dimmed'>
                Say "yes" or "confirm" to create these time entries, or "no" to
                cancel.
              </Text>
            </Stack>
          </Stack>
        )}
      </div>
    </div>
  );
};
